/**
 * UntitledUI Charts Base Components
 * Source: npx untitledui@latest add charts-base
 *
 * Custom extensions added at the bottom of this file.
 */
import type { ReactNode } from 'react';
import type {
  LegendPayload,
  Props as LegendContentProps,
} from 'recharts/types/component/DefaultLegendContent';
import type {
  Formatter,
  NameType,
  Payload,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';

import { cx } from '@/utils/cx';

const LEGEND_SWATCH_DOT = 'h-2 w-2 rounded-full';
const LEGEND_SWATCH_LINE = 'h-0.5 w-3 rounded-full';

/**
 * Selects evenly spaced items from an array. Used for rendering
 * certain number of x-axis labels.
 * @param dataArray - The array of items to select from.
 * @param count - The number of items to select.
 * @returns The selected items.
 */
// eslint-disable-next-line react-refresh/only-export-components -- helper function co-located with component
export const selectEvenlySpacedItems = <T extends readonly unknown[]>(
  dataArray: T,
  count: number
): Array<T[number]> => {
  if (!dataArray || dataArray.length === 0) {
    return [];
  }

  const selectedItems: Array<T[number]> = [];

  if (dataArray.length === 1) {
    for (let i = 0; i < count; i++) {
      selectedItems.push(dataArray[0]);
    }
    return selectedItems;
  }

  for (let i = 0; i < count; i++) {
    const targetIndex = Math.round(
      (i * (dataArray.length - 1)) / Math.max(count - 1, 1)
    );
    const boundedIndex = Math.max(
      0,
      Math.min(targetIndex, dataArray.length - 1)
    );
    selectedItems.push(dataArray[boundedIndex]);
  }

  return selectedItems;
};

/**
 * Renders the legend content for a chart.
 * @param reversed - Whether to reverse the payload.
 * @param payload - The payload of the legend.
 * @param align - The alignment of the legend.
 * @param layout - The layout of the legend.
 * @param className - The class name of the legend.
 * @returns The legend content.
 */
export const ChartLegendContent = ({
  reversed,
  payload,
  align,
  layout,
  className,
}: LegendContentProps & { reversed?: boolean; className?: string }) => {
  const items = reversed ? payload?.slice().reverse() : payload;

  const verticalAlignClass =
    { center: 'items-center', right: 'items-end' }[align as string] ??
    'items-start';
  const horizontalAlignClass =
    { center: 'justify-center', right: 'justify-end' }[align as string] ??
    'justify-start';

  return (
    <ul
      className={cx(
        'flex',
        layout === 'vertical'
          ? `flex-col gap-1 pl-4 ${verticalAlignClass}`
          : `flex-row gap-3 ${horizontalAlignClass}`,
        className
      )}
    >
      {items?.map((entry: LegendPayload) => (
        <li
          className='flex items-center gap-2 text-sm text-tertiary'
          key={`${entry.value ?? 'legend'}-${entry.dataKey ?? String(entry.color)}`}
        >
          <span
            aria-hidden
            className={cx(
              entry.type === 'line' ? LEGEND_SWATCH_LINE : LEGEND_SWATCH_DOT,
              (entry.payload as { className?: string })?.className
            )}
            style={{ backgroundColor: entry.color }}
          />
          {entry.value}
        </li>
      ))}
    </ul>
  );
};

interface ChartTooltipContentProps {
  active?: boolean;
  isRadialChart?: boolean;
  isPieChart?: boolean;
  label?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- recharts payload prop is not typed correctly in the library
  payload?: any;
  formatter?: Formatter<ValueType, NameType>;
  labelFormatter?: (
    label: ReactNode,
    payload: ReadonlyArray<Payload<ValueType, NameType>>
  ) => ReactNode;
}

export const ChartTooltipContent = ({
  active,
  payload,
  label,
  isRadialChart,
  isPieChart,
  formatter,
  labelFormatter,
}: ChartTooltipContentProps) => {
  const canRender = active && payload && payload.length;

  if (!canRender) {
    return null;
  }

  const isSingleDataPoint = payload.length === 1;

  // If it's a single data point, we use the value as the title and
  // the name as the secondary title.
  let title = isSingleDataPoint ? payload[0].value : label;

  let secondaryTitle;
  if (isSingleDataPoint) {
    if (isRadialChart) {
      secondaryTitle = payload[0].payload.name;
    } else if (isPieChart) {
      secondaryTitle = payload[0].name;
    } else {
      secondaryTitle = label;
    }
  } else {
    secondaryTitle = payload;
  }

  if (isSingleDataPoint && formatter) {
    title = formatter(
      title,
      payload?.[0].name || label,
      payload[0],
      0,
      payload
    );
  } else if (labelFormatter) {
    title = labelFormatter(title, payload);
  }
  if (isSingleDataPoint && labelFormatter) {
    secondaryTitle = labelFormatter(secondaryTitle, payload);
  }

  return (
    <div className='flex flex-col gap-0.5 rounded-lg bg-primary-solid px-3 py-2 shadow-lg'>
      <p className='text-xs font-semibold text-white'>{title}</p>

      {secondaryTitle && Array.isArray(secondaryTitle) && (
        <div>
          {secondaryTitle.map((entry, index) => (
            <p
              key={`${entry.name ?? 'tooltip'}-${entry.dataKey ?? String(entry.color)}`}
              className={cx('text-xs text-tooltip-supporting-text')}
            >
              {`${entry.name}: ${formatter ? formatter(entry.value, entry.name, entry, index, entry.payload) : entry.value}`}
            </p>
          ))}
        </div>
      )}
      {secondaryTitle && !Array.isArray(secondaryTitle) && (
        <p className='text-xs text-tooltip-supporting-text'>{secondaryTitle}</p>
      )}
    </div>
  );
};

interface ChartActiveDotProps {
  cx?: number;
  cy?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- recharts payload prop is not typed correctly in the library
  payload?: any;
}

export const ChartActiveDot = ({
  cx: dotCx = 0,
  cy: dotCy = 0,
}: ChartActiveDotProps) => {
  const size = 12;

  return (
    <svg
      x={dotCx - size / 2}
      y={dotCy - size / 2}
      width={size}
      height={size}
      viewBox='0 0 12 12'
      fill='none'
    >
      <rect
        x='2'
        y='2'
        width='8'
        height='8'
        rx='6'
        className='fill-bg-primary stroke-utility-brand-600'
        strokeWidth='2'
      />
    </svg>
  );
};

// =============================================================================
// Custom Extensions (not part of UntitledUI)
// =============================================================================

interface SimpleChartTooltipProps {
  active?: boolean;
  payload?: Payload<ValueType, NameType>[];
  formatter?: Formatter<ValueType, NameType>;
}

/**
 * Simple tooltip for single-series charts like pie charts.
 * Custom extension - not part of UntitledUI.
 */
export const SimpleChartTooltip = ({
  active,
  payload,
  formatter,
}: SimpleChartTooltipProps) => {
  if (!active || !payload?.length) {
    return null;
  }

  const entry = payload[0];
  const value =
    formatter && entry.value !== undefined && entry.name !== undefined
      ? formatter(entry.value, entry.name, entry, 0, payload)
      : entry.value;

  return (
    <div className='flex flex-col gap-0.5 rounded-lg bg-primary-solid px-3 py-2 shadow-lg'>
      <p className='text-xs text-tooltip-supporting-text'>{entry.name}</p>
      <p className='text-sm font-semibold text-white'>{value}</p>
    </div>
  );
};
